import { defineStore } from 'pinia';
import { useToast } from 'vue-toast-notification';
import { useLoadingStore } from '@/pinia/loading.module';
import { getLocaleMessage } from '@/services/MessagesService';
import { getNestedValue } from '@/helpers/sort';
import Client from '@/Models/Client';

import * as service from '@/services';

const $toast = useToast();

export const useClientsStore = defineStore('clients', {
    state: () => ({
        /**Datos de tabla */
        sortKey: '',
        sortOrder: 1, // 1 for ascending, -1 for descending
        itemsPerPage: 10, //Por default 10 por pagina
        currentPage: 1,
        searchString: '',
        clients: [],
        client: new Client({})
    }),
    getters: {
        getAllClients: state => state.clients,
        filteredS: (state) => {
            if (!state.searchString) return state.clients;

            const searchLower = state.searchString.toLowerCase();
            return state.clients.filter(client => {
                return (
                    client?.referenceNumber?.toLowerCase().includes(searchLower) ||
                    client?.creditFacility?.loanApplication?.referenceNumber?.toLowerCase().includes(searchLower) ||
                    client?.creditFacility?.creditScore?.referenceNumber?.toLowerCase().includes(searchLower) ||
                    client?.creditFacility?.creditScore?.score?.toString().includes(searchLower) || // Cambio importante aquí
                    client?.creditFacility?.referenceNumber?.toLowerCase().includes(searchLower) ||
                    client?.status?.name?.toLowerCase().includes(searchLower)
                );
            });
        },
        /**
        * Ordena los registros de la tabla 
        */
        sortedS: (state) => {
            // Logica de ordenamiento
            let sortedList = [...state.filteredS];
            if (state.sortKey) {
                sortedList.sort((a, b) => {

                    // Ordenamiento normal para otras columnas
                    let aValue = getNestedValue(a, state.sortKey);
                    let bValue = getNestedValue(b, state.sortKey);

                    if (aValue < bValue) return -1 * state.sortOrder;
                    if (aValue > bValue) return 1 * state.sortOrder;
                    return 0;
                });
            }
            return sortedList;
        },
        /**
         * Aplica paginación a la lista ordenada.
         */
        paginatedS: (state) => {
            if (!state.sortedS || state.sortedS.length === 0) return [];
            let start = (state.currentPage * state.itemsPerPage) - state.itemsPerPage;
            return state.sortedS.slice(start, start + state.itemsPerPage);
        },
        getClientById: state => (id = 0) => (id ? state.clients.find(p => p.Id === id) : new Object()) ?? new Object(),
    },
    actions: {
        async fetchClients() {
            try {
                useLoadingStore().loading = true;
                const { data } = await service.prospectsApi.get(`/api/clients`);
                if (data && data.status) {
                    const response = data.data;
                    const clients = response.map(cl => new Client(cl));
                    this.clients = clients;
                }
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        async updateClientsList() {
            try {
                useLoadingStore().loading = true;
                const { data } = await service.prospectsApi.post(`/api/clients`);
                if (data && data.status) {
                    const response = data.data;
                    const newClients = response.map(cl => new Client(cl));

                    // Filtrar solo los clientes que no existen en la lista actual
                    const clientsToAdd = newClients.filter(newClient =>
                        !this.clients.some(existingClient =>
                            existingClient.id === newClient.id
                        )
                    );

                    // Agregar solo los nuevos clientes
                    this.clients.push(...clientsToAdd);
                    return this.client;
                }
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        async changeStatusClient(clientData) {
            try {
                useLoadingStore().loading = true;
                const { data } = await service.prospectsApi.put(`/api/clients/status`, {
                    clientId: clientData.id,
                    statusId: clientData.statusId
                });

                if (data && data.status) {
                    $toast.success(getLocaleMessage('clientRequest', 'editingSuccess'));
                    await this.fetchClients();
                    return data;
                } else {
                    $toast.error(data?.error || getLocaleMessage('clientRequest', 'editingError'));
                    await this.fetchClients();
                    return data;
                }
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        handleError(error) {
            this.errors = error;
            if (error.response) {
                console.log("Inspec handle ERROR IF: ", error)
                let msg = error?.response?.data?.error || getLocaleMessage('common', 'responseError');
                $toast.error(msg);
            } else if (error.request) {
                console.log("Inspec handle ERROR ELSE IF: ", error)
                let msg = error?.response?.data?.error || getLocaleMessage('common', 'networkError');
                $toast.error(msg);
            } else {
                console.log("Inspec handle ERROR ELSE: ", error)
                let msg = error?.response?.data?.error || getLocaleMessage('common', 'generalError');
                $toast.error(msg);
            }
        },
        /**
        * Cambia el orden de las columnas en la tabla.
        */
        sortBy(key) {
            if (this.sortKey === key) {
                this.sortOrder *= -1;
            } else {
                this.sortOrder = 1;
            }
            this.sortKey = key;
            this.currentPage = 1; // Resetear a la primera página al ordenar
        },
        /**
         * Establece la página actual del paginador.
         */
        setCurrentPage(page) {
            this.currentPage = page;
        },
        setSearchString(value) {
            this.searchString = value;
            this.currentPage = 1; // Resetear paginación al buscar
        },
        setSelectedClient(id) {
            this.client = this.getClientById(id);
        },
    }
});